import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { tafqit, type Branding, type CompanyProfile, type TenantSettings } from '@goyone/shared';
import { api, qs } from '../../lib/api';
import { closePrintView } from '../../lib/printclose';
import { date, dateTime, money } from '../../lib/format';
import { Logo } from '../../components/Logo';
import { PrintSheet, useDocumentPaper } from '../../components/DocumentPaper';
import '../../styles/print.css';

/**
 * كشف الحساب المطبوع — من عائلة مستندات الشركة نفسها.
 *
 * الترويسة والهوية والألوان هي ترويسة الفاتورة وعرض السعر سواء، فما
 * يصل العميل يبدو صادرًا من جهة واحدة. والأرقام هي أرقام الكشف
 * المخزَّن وثيقةَ لحظته — لا إعادة حساب عند الطباعة.
 */

const MONTHS_AR = [
  'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
  'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر',
];

interface StatementRow {
  id: string;
  periodYear: number;
  periodMonth: number;
  contractedTotal: string;
  invoicedTotal: string;
  paidTotal: string;
  balance: string;
  invoicedInMonth: string;
  paidInMonth: string;
  issuedAt: string;
}

interface Profile {
  tenant: { name: string; currency: string };
  branding: Branding;
  company: CompanyProfile;
  settings: TenantSettings;
}

export function PrintStatement() {
  const { accountId = '', sid = '' } = useParams<{ accountId: string; sid: string }>();

  const statements = useQuery({
    queryKey: ['account', accountId, 'statements'],
    queryFn: () => api<StatementRow[]>(`/crm/accounts/${accountId}/statements`),
  });
  const accountQ = useQuery({
    queryKey: ['account', accountId],
    queryFn: () => api<{ account: Record<string, any> }>(`/crm/accounts/${accountId}`),
  });
  const profile = useQuery({
    queryKey: ['settings'],
    queryFn: () => api<Profile>('/settings'),
  });
  /* ورقةُ الشركة (ليتر هيد) إن رُفعت: تحلّ محلّ ترويسة النظام وتذييله. كشفُ الحساب على ورقته إن رُفعت، وإلّا على العامّة. */
  const { paper, ready: paperReady } = useDocumentPaper('statement');

  /*
   * تفصيل حركة الشهر: فواتيره وتحصيلاته بأرقامها وتواريخها — وقائعُ
   * تاريخية بتواريخها لا تتبدّل، فجلبُها عند الطباعة لا يخون «وثيقة
   * اللحظة» التي تحفظها الإجماليات.
   */
  const invoicesQ = useQuery({
    queryKey: ['statement-detail', 'invoices', accountId],
    queryFn: () =>
      api<{ items: Array<Record<string, any>> }>(
        `/finance/invoices${qs({ accountId, pageSize: 100 })}`,
      ),
  });
  const paymentsQ = useQuery({
    queryKey: ['statement-detail', 'payments', accountId],
    queryFn: () =>
      api<{ items: Array<Record<string, any>> }>(
        `/finance/payments${qs({ accountId, pageSize: 100 })}`,
      ),
  });

  const st = (statements.data ?? []).find((s) => s.id === sid);
  const account = accountQ.data?.account;
  const ready = st && account && profile.data && paperReady;

  if (statements.isError || accountQ.isError || profile.isError) {
    return (
      <div className="print-page">
        <p>تعذّر تحميل الكشف.</p>
      </div>
    );
  }

  if (!ready) {
    return (
      <div className="print-page">
        <p>جارٍ التحضير…</p>
      </div>
    );
  }

  const { branding, company, settings, tenant } = profile.data!;
  const currency = settings.currencyLabel || 'ج.م';
  const monthName = `${MONTHS_AR[(st.periodMonth ?? 1) - 1]} ${st.periodYear}`;
  const balance = Number(st.balance);

  /* حركة الشهر تفصيلًا: فاتورةٌ مدينة وتحصيلٌ دائن، مرتّبةً بتاريخها. */
  const start = Date.UTC(st.periodYear, st.periodMonth - 1, 1);
  const end = Date.UTC(st.periodYear, st.periodMonth, 1);
  const inPeriod = (iso: string | null | undefined) => {
    const t = iso ? new Date(iso).getTime() : NaN;
    return Number.isFinite(t) && t >= start && t < end;
  };
  const movement = [
    ...(invoicesQ.data?.items ?? [])
      .filter((i) => inPeriod(i.issuedAt) && i.status !== 'DRAFT' && i.status !== 'CANCELLED')
      .map((i) => ({
        at: i.issuedAt as string,
        label: `فاتورة رقم ${i.number}`,
        debit: Number(i.total ?? 0),
        credit: 0,
      })),
    ...(paymentsQ.data?.items ?? [])
      .filter((p) => inPeriod(p.paidAt))
      .map((p) => ({
        at: p.paidAt as string,
        label: `تحصيل رقم ${p.number}${p.invoice?.number ? ` — عن فاتورة ${p.invoice.number}` : ''}`,
        debit: 0,
        credit: Number(p.amount ?? 0),
      })),
  ].sort((a, b) => (a.at < b.at ? -1 : 1));

  const rows: Array<[string, string]> = [
    ['إجمالي التعاقدات الموقَّعة', money(st.contractedTotal, currency)],
    ['إجمالي المفوتر (تراكمي)', money(st.invoicedTotal, currency)],
    ['فُوتر خلال الشهر', money(st.invoicedInMonth, currency)],
    ['إجمالي المدفوعات (تراكمي)', money(st.paidTotal, currency)],
    ['حُصِّل خلال الشهر', money(st.paidInMonth, currency)],
  ];

  return (
    <div
      className="print-root"
      style={{
        ['--doc-primary' as string]: branding.primary,
        ['--doc-ink' as string]: branding.ink,
      }}
    >
      <div className="print-toolbar no-print">
        <button className="btn btn-primary" onClick={() => window.print()}>
          طباعة أو حفظ PDF
        </button>
        <button className="btn" onClick={closePrintView}>
          إغلاق
        </button>
        <span className="small mute">اختر «حفظ كـ PDF» في نافذة الطباعة للحصول على ملف.</span>
      </div>

      <PrintSheet paper={paper}>
        <header className="doc-header">
          <div className="doc-brand">
            {branding.logoUrl ? (
              <img src={branding.logoUrl} alt="" className="doc-logo" />
            ) : (
              <Logo size={52} />
            )}
            <div>
              <div className="doc-company">{company.legalName || tenant.name}</div>
              {branding.documentHeader && (
                <div className="doc-tagline">{branding.documentHeader}</div>
              )}
              <div className="doc-company-meta">
                {company.address && <div>{company.address}</div>}
                <div className="doc-inline">
                  {company.phone && <span className="num">{company.phone}</span>}
                  {company.email && <span className="num">{company.email}</span>}
                </div>
              </div>
            </div>
          </div>

          <div className="doc-title-block">
            <div className="doc-kind">كشف حساب</div>
            <div className="doc-number">{monthName}</div>
          </div>
        </header>

        <section className="doc-parties">
          <div className="doc-party">
            <div className="doc-label">العميل</div>
            <div className="doc-party-name">{account.legalName || account.name}</div>
            {account.address && <div>{account.address}</div>}
            <div className="doc-inline">
              {account.phone && <span className="num">{account.phone}</span>}
              {account.email && <span className="num">{account.email}</span>}
            </div>
            {account.taxNumber && (
              <div>
                الرقم الضريبي: <span className="num">{account.taxNumber}</span>
              </div>
            )}
          </div>

          <div className="doc-dates">
            <div className="doc-date-row">
              <span className="doc-label">الفترة</span>
              <span>{monthName}</span>
            </div>
            <div className="doc-date-row">
              <span className="doc-label">صدر في</span>
              <span>{dateTime(st.issuedAt)}</span>
            </div>
          </div>
        </section>

        <table className="doc-table">
          <thead>
            <tr>
              <th>البيان</th>
              <th style={{ width: 160 }}>المبلغ</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(([label, value]) => (
              <tr key={label}>
                <td>{label}</td>
                <td className="num end">{value}</td>
              </tr>
            ))}
            <tr>
              <td className="strong">الرصيد المتبقي</td>
              <td className="num end strong">{money(st.balance, currency)}</td>
            </tr>
          </tbody>
        </table>

        {/* حركة الشهر تفصيلًا — بعد الإجماليات: التاريخ والمستند والمبلغ. */}
        <h3 style={{ marginTop: 20, marginBottom: 8 }}>حركة الشهر تفصيلًا</h3>
        <table className="doc-table">
          <thead>
            <tr>
              <th style={{ width: 110 }}>التاريخ</th>
              <th>البيان</th>
              <th style={{ width: 130 }}>مدين (عليه)</th>
              <th style={{ width: 130 }}>دائن (له)</th>
            </tr>
          </thead>
          <tbody>
            {movement.length === 0 && (
              <tr>
                <td colSpan={4} className="center">
                  لا حركة خلال هذا الشهر.
                </td>
              </tr>
            )}
            {movement.map((m, i) => (
              <tr key={i}>
                <td className="num center">{date(m.at)}</td>
                <td>{m.label}</td>
                <td className="num end">{m.debit ? money(m.debit, currency) : '—'}</td>
                <td className="num end">{m.credit ? money(m.credit, currency) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* المتبقي بالحروف — كما تُكتب المبالغ في مستندات الشركة. */}
        <div className="doc-words" style={{ marginTop: 16 }}>
          <div className="doc-label">الرصيد المتبقي بالحروف</div>
          <div className="doc-words-text">
            {balance > 0
              ? tafqit(balance, tenant.currency)
              : 'لا رصيد متبقٍّ في ذمة العميل حتى نهاية هذه الفترة.'}
          </div>
        </div>

        <p className="small" style={{ marginTop: 16, color: '#666' }}>
          هذا الكشف وثيقة لحظة إصداره ({date(st.issuedAt)}) — أرقامه محفوظة كما حُسبت ولا
          تتغيّر بأثرٍ رجعي.
        </p>
      </PrintSheet>
    </div>
  );
}
